// src/pages/Hosts.js - Updated with region filter support
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Grid,
  Box,
  Divider,
  Card,
  Button,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  BarChart as ChartIcon,
} from '@mui/icons-material';

// Import custom hooks and components
import { useAppData } from '../contexts/AppDataContext';
import PageHeader from '../components/common/PageHeader';
import SearchFilterBar from '../components/common/SearchFilterBar';
import LoadingOverlay from '../components/common/LoadingOverlay';
import ResourceProgress from '../components/common/ResourceProgress';
import StatusChip from '../components/common/StatusChip';
import api from '../services/api'; // Add this import for direct API calls

export default function Hosts() {
  const theme = useTheme();
  const location = useLocation();
  
  // Parse URL for region filter
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [region, setRegion] = useState(
    queryParams.get('region') || localStorage.getItem('selectedRegion') || ''
  );
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  
  // Get data from context
  const { 
    hosts,
    regions,
    loading,
    fetchRegions,
    fetchHosts,
    fetchSystemMetricsForHosts,
    systemMetrics,
    refreshAllData
  } = useAppData();
  
  // Add a debug effect to monitor systemMetrics changes
  useEffect(() => {
    if (systemMetrics && Object.keys(systemMetrics).length > 0) {
      console.log('System metrics updated in state:', Object.keys(systemMetrics).length, 'entries');
    }
  }, [systemMetrics]);
  
  // Ensure system metrics are loaded even if they weren't loaded during the initial fetch
  useEffect(() => {
    const loadMetrics = async () => {
      if (hosts && hosts.length > 0 && (!systemMetrics || Object.keys(systemMetrics).length === 0)) {
        console.log('Loading system metrics separately for', hosts.length, 'hosts');
        try {
          await fetchSystemMetricsForHosts(hosts);
          
          // If still no metrics, try a direct API call to diagnose the issue
          if (!systemMetrics || Object.keys(systemMetrics).length === 0) {
            console.log('Still no metrics, trying direct API call');
            try {
              // Group hosts by region for batch requests
              const hostsByRegion = {};
              hosts.forEach(host => {
                const region = host.split('-')[0];
                if (!hostsByRegion[region]) {
                  hostsByRegion[region] = [];
                }
                hostsByRegion[region].push(host);
              });
              
              // Make direct API calls for each region
              const metricsData = {};
              for (const [region, regionHosts] of Object.entries(hostsByRegion)) {
                const url = `/api/system_metrics_batch?hosts=${regionHosts.join(',')}&region=${region}`;
                console.log('Direct API call to:', url);
                const response = await api.get(url);
                console.log('Direct API response:', response.data);
                Object.assign(metricsData, response.data);
              }
              
              console.log('Direct API metrics data:', metricsData);
              // Now we have the metrics directly, we can use them
              if (Object.keys(metricsData).length > 0) {
                console.log('Got metrics directly from API:', Object.keys(metricsData).length, 'entries');
                // We'll use these metrics directly in the component state
                setDirectMetrics(metricsData);
              }
            } catch (error) {
              console.error('Error in direct API call:', error);
            }
          }
        } catch (error) {
          console.error('Error loading system metrics:', error);
        }
      }
    };
    
    loadMetrics();
  }, [hosts, systemMetrics, fetchSystemMetricsForHosts]);
  
  // Add state for direct metrics data
  const [directMetrics, setDirectMetrics] = useState({});
  
  // Use direct metrics if available, otherwise fall back to context metrics
  const effectiveMetrics = useMemo(() => {
    if (directMetrics && Object.keys(directMetrics).length > 0) {
      console.log('Using direct metrics:', Object.keys(directMetrics).length, 'entries');
      return directMetrics;
    }
    return systemMetrics;
  }, [systemMetrics, directMetrics]);
  
  // Load hosts data
  useEffect(() => {
    const loadData = async () => {
      try {
        // First fetch regions
        await fetchRegions();
        
        // Then fetch hosts, ensuring we have region data first
        const hostsList = await fetchHosts(region);
        console.log('Fetched hosts:', hostsList, 'for region:', region); // Debug log
        
        // Only proceed with system metrics if we have hosts
        if (hostsList && hostsList.length > 0) {
          const metricsData = await fetchSystemMetricsForHosts(hostsList);
          console.log('Fetched system metrics:', Object.keys(metricsData).length, 'entries');
        } else {
          console.warn('No hosts found to fetch metrics for');
        }
      } catch (error) {
        console.error('Error loading host data:', error);
      }
    };
    
    loadData();
  }, [region, fetchRegions, fetchHosts, fetchSystemMetricsForHosts]);
  
  // Handle refresh
  const handleRefresh = () => {
    console.log('Refreshing data with region:', region);
    
    // Force clear the systemMetrics in state to trigger a re-fetch
    if (typeof refreshAllData === 'function') {
      // First try with the current region
      refreshAllData({ region, forceRefresh: true });
      
      // Add a small delay and check if data was loaded
      setTimeout(() => {
        if (!hosts || hosts.length === 0 || !systemMetrics || Object.keys(systemMetrics).length === 0) {
          console.log('No hosts or metrics after refresh, trying again with empty region');
          refreshAllData({ region: '', forceRefresh: true });
        }
      }, 1000);
    } else {
      console.error('refreshAllData is not a function:', typeof refreshAllData);
    }
  };
  
  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'table' : 'grid');
  };
  
  // Update URL when region changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (region) {
      params.set('region', region);
    } else {
      params.delete('region');
    }
    
    const newUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', newUrl);
    
    // Store in localStorage for persistence
    if (region) {
      localStorage.setItem('selectedRegion', region);
    } else {
      localStorage.removeItem('selectedRegion');
    }
  }, [region, location.pathname, location.search]);
  
  // Filter hosts based on search term and region
  const filteredHosts = useMemo(() => {
    if (!hosts || !hosts.length) return [];
    
    const filtered = hosts.filter(hostname => {
      // Check if hostname matches search term
      const matchesSearch = hostname.toLowerCase().includes(searchTerm.toLowerCase());
      
      // If region is not selected or if host is in the selected region (case-insensitive)
      const matchesRegion = !region || hostname.toLowerCase().startsWith(region.toLowerCase());
      
      return matchesSearch && matchesRegion;
    });
    
    console.log('Filtering hosts:', { region, totalHosts: hosts.length, filteredCount: filtered.length });
    return filtered;
  }, [hosts, searchTerm, region]);
  
  // Get region options for filter
  const regionOptions = useMemo(() => {
    const options = [{ value: '', label: 'All Regions' }];
    if (regions && regions.length) {
      regions.forEach(region => options.push({ value: region, label: region }));
    }
    return options;
  }, [regions]);
  
  // Helper function to determine color based on value
  const getStatusColor = (value, type) => {
    if (value === null || value === undefined) return theme.palette.grey[500];
    
    if (type === 'status') {
      return value === 'active' ? theme.palette.success.main : theme.palette.error.main;
    }
    
    if (type === 'cpu' || type === 'memory' || type === 'disk') {
      if (value > 90) return theme.palette.error.main;
      if (value > 75) return theme.palette.warning.main;
      return theme.palette.success.main;
    }
    
    return theme.palette.primary.main;
  };

  // Helper function to get initial from hostname
  const getInitial = (hostname) => {
    if (!hostname) return 'U'; // Fallback
    const parts = hostname.split('-');
    return parts[0].charAt(0).toUpperCase();
  };

  // Helper to get gradient for background
  const getBackgroundGradient = (hostname) => {
    if (!hostname) return '';
    
    const gradients = [
      `linear-gradient(135deg, ${theme.palette.primary.main}0d, transparent)`,
      `linear-gradient(135deg, ${theme.palette.secondary.main}0d, transparent)`,
      `linear-gradient(135deg, ${theme.palette.tertiary?.main || '#ffb74d'}0d, transparent)`,
      `linear-gradient(135deg, ${theme.palette.quaternary?.main || '#ff9966'}0d, transparent)`,
      `linear-gradient(135deg, ${theme.palette.info.main}0d, transparent)`,
    ];
    
    const sum = hostname.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[sum % gradients.length];
  };

  // Helper function to get avatar background color
  const getHostColor = (hostname) => {
    if (!hostname) return `${theme.palette.primary.main}1a`; // Fallback color
    
    // Map different host prefixes to colors from the theme
    const prefixColors = {
      'R': `${theme.palette.primary.main}1a`,    // RUH
      'A': `${theme.palette.secondary.main}1a`,  // AUH
      'N': `${theme.palette.tertiary?.main || '#ffb74d'}1a`,   // New
      'B': `${theme.palette.quaternary?.main || '#ff9966'}1a`, // BOM
      'J': `${theme.palette.info.main}1a`,       // JNB
      'S': `${theme.palette.success.main}1a`,    // SGP
    };

    const firstLetter = hostname.charAt(0).toUpperCase();
    return prefixColors[firstLetter] || `${theme.palette.primary.main}1a`;
  };

  // Helper function to get avatar text color
  const getHostTextColor = (hostname) => {
    if (!hostname) return theme.palette.primary.main; // Fallback color
    
    // Map different host prefixes to colors from the theme
    const prefixColors = {
      'R': theme.palette.primary.main,    // RUH
      'A': theme.palette.secondary.main,  // AUH
      'N': theme.palette.tertiary?.main || '#ffb74d',   // New
      'B': theme.palette.quaternary?.main || '#ff9966', // BOM
      'J': theme.palette.info.main,       // JNB
      'S': theme.palette.success.main,    // SGP
    };

    const firstLetter = hostname.charAt(0).toUpperCase();
    return prefixColors[firstLetter] || theme.palette.primary.main;
  };

  // Safely get system metrics for a host
  const getHostMetrics = (hostname) => {
    // Check if effectiveMetrics and the specific host exists
    if (!effectiveMetrics || !Object.keys(effectiveMetrics).length || !hostname) {
      console.log('No metrics found for host:', hostname, 'effectiveMetrics:', effectiveMetrics ? Object.keys(effectiveMetrics).length : 'null');
      // Use consistent placeholder values based on hostname to avoid random changes
      const hash = hostname ? hostname.split('').reduce((a, b) => a + b.charCodeAt(0), 0) : 0;
      return {
        cpu: 10 + (hash % 20), // Consistent value between 10-30%
        memory: 20 + (hash % 30), // Consistent value between 20-50%
        disk: 30 + (hash % 40), // Consistent value between 30-70%
        status: 'inactive',
        region: hostname ? hostname.split('-')[0] : '',
        isPlaceholder: true // Flag to indicate this is placeholder data
      };
    }
    
    // Return metrics if they exist, otherwise return default values
    return effectiveMetrics[hostname] || {
      // Use consistent placeholder values based on hostname to avoid random changes
      cpu: 10 + (hostname.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 20),
      memory: 20 + (hostname.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 30),
      disk: 30 + (hostname.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 40),
      status: 'inactive',
      region: hostname.split('-')[0],
      isPlaceholder: true // Flag to indicate this is placeholder data
    };
  };

  // Navigate back to dashboard with current region
  const navigateBackToDashboard = () => {
    window.location.href = region ? `/?region=${region}` : '/';
  };

  // Create filter actions element for the header
  const filterActions = (
    <Box display="flex" gap={2} flexWrap="wrap">
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={navigateBackToDashboard}
        sx={{
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 'medium',
          borderColor: alpha(theme.palette.primary.main, 0.3),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          },
        }}
      >
        Back to Dashboard
      </Button>
      
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterOptions={regionOptions}
        filterValue={region}
        onFilterChange={setRegion}
        onRefresh={handleRefresh}
        placeholder="Search hosts..."
        filterLabel="Region"
      />
      
      <Tooltip title={viewMode === 'grid' ? 'Table View' : 'Grid View'}>
        <IconButton 
          onClick={toggleViewMode}
          sx={{
            backgroundColor: `${theme.palette.primary.main}1a`,
            color: theme.palette.primary.main,
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.main}33`,
            },
          }}
        >
          {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          borderRadius: '20px',
          padding: { xs: 2, md: 3 },
          backgroundImage: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5,
            }}
          >
            {region ? `${region} Hosts` : 'Listener Management'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {region ? `Monitoring hosts in ${region} region` : 'Monitor and manage all hosts'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {filterActions}
        </Box>
      </Box>

      {/* Loading indicator */}
      <LoadingOverlay 
        loading={loading.hosts || loading.systemMetrics}
        message="Loading host data..."
      >
        {/* No Results */}
        {filteredHosts.length === 0 && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            height={300}
            sx={{
              borderRadius: '16px',
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
              p: 4,
            }}
          >
            <Box 
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: alpha(theme.palette.primary.main, 0.7),
                mb: 2,
              }}
            >
              <StorageIcon fontSize="large" />
            </Box>
            <Typography variant="h5" fontWeight="medium" textAlign="center" mb={1}>
              No hosts found
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Try adjusting your search filters or changing the region selection
            </Typography>
          </Box>
        )}

        {/* Grid View */}
        {filteredHosts.length > 0 && viewMode === 'grid' && (
          <Grid container spacing={3} key={`grid-${effectiveMetrics ? Object.keys(effectiveMetrics).length : 0}`}>
            {filteredHosts.map((hostname) => {
              const metrics = getHostMetrics(hostname);
              
              return (
                <Grid item xs={12} sm={6} md={4} key={hostname}>
                  <Card sx={{ 
                    borderRadius: '16px', 
                    height: '100%',
                    backgroundImage: getBackgroundGradient(hostname),
                    backgroundColor: theme.palette.background.paper, 
                    boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                    backdropFilter: 'blur(8px)',
                    transition: 'transform 0.2s ease-in-out',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 15px 30px ${alpha(theme.palette.common.black, 0.12)}`,
                    },
                    ...(metrics.isPlaceholder && {
                      border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
                    }),
                  }}>
                    <Box sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          sx={{
                            width: 50,
                            height: 50,
                            bgcolor: getHostColor(hostname),
                            color: getHostTextColor(hostname),
                            fontWeight: 'bold',
                            mr: 2,
                            fontSize: '1.5rem',
                          }}
                        >
                          {getInitial(hostname)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="medium" noWrap>
                            {hostname}
                          </Typography>
                          <Box display="flex" alignItems="center">
                            <Chip 
                              size="small" 
                              label={metrics.region || 'Unknown'} 
                              sx={{
                                backgroundColor: `${theme.palette.primary.main}1a`,
                                color: theme.palette.primary.main,
                                fontWeight: 'medium',
                                borderRadius: '6px',
                                height: 20,
                              }}
                            />
                            <Box 
                              sx={{ 
                                ml: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                color: getStatusColor(metrics.status, 'status')
                              }}
                            >
                              {metrics.status === 'active' ? (
                                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                              ) : (
                                <ErrorIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                              )}
                              <Typography variant="caption" fontWeight="bold">
                                {metrics.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
                                {metrics.isPlaceholder && ' (Est.)'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ mb: 2, opacity: 0.1 }} />
                      
                      <Box>
                        <ResourceProgress
                          label="CPU Usage"
                          value={metrics.cpu || 0}
                          size="medium"
                        />
                        
                        <Box mt={2}>
                          <ResourceProgress
                            label="Memory Usage"
                            value={metrics.memory || 0}
                            size="medium"
                          />
                        </Box>
                        
                        <Box mt={2}>
                          <ResourceProgress
                            label="Disk Usage"
                            value={metrics.disk || 0}
                            size="medium"
                          />
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 2, opacity: 0.1 }} />
                      
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Link 
                          to={`/region/${metrics.region}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Button 
                            variant="contained" 
                            size="small" 
                            startIcon={<ChartIcon />}
                            sx={{
                              backgroundColor: `${theme.palette.primary.main}`,
                              color: '#fff',
                              fontWeight: 'medium',
                              borderRadius: '8px',
                              boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                              '&:hover': {
                                backgroundColor: `${theme.palette.primary.dark}`,
                                boxShadow: `0 6px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
                              },
                              textTransform: 'none',
                            }}
                          >
                            View Metrics
                          </Button>
                        </Link>
                        <Tooltip title="More Options">
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: `${theme.palette.background.default}80`,
                              color: theme.palette.text.secondary,
                              borderRadius: '8px',
                              '&:hover': {
                                backgroundColor: `${theme.palette.primary.main}1a`,
                                color: theme.palette.primary.main,
                              },
                            }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Table View */}
        {filteredHosts.length > 0 && viewMode === 'table' && (
          <Card sx={{ 
            borderRadius: '16px',
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
            overflow: 'hidden',
          }} key={`table-${effectiveMetrics ? Object.keys(effectiveMetrics).length : 0}`}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{
                    '& th': {
                      backgroundColor: `${theme.palette.background.paper}cc`,
                      backdropFilter: 'blur(8px)',
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      borderBottom: `1px solid ${theme.palette.divider}1a`,
                    }
                  }}>
                    <TableCell>Hostname</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>CPU Usage</TableCell>
                    <TableCell>Memory Usage</TableCell>
                    <TableCell>Disk Usage</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHosts.map((hostname) => {
                    const metrics = getHostMetrics(hostname);
                    
                    return (
                      <TableRow 
                        key={hostname} 
                        hover
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: `${theme.palette.primary.main}0a`,
                          },
                          '& td': {
                            borderBottom: `1px solid ${theme.palette.divider}0d`,
                          }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: '0.875rem',
                                bgcolor: getHostColor(hostname),
                                color: getHostTextColor(hostname),
                                fontWeight: 'bold',
                                mr: 1.5,
                              }}
                            >
                              {getInitial(hostname)}
                            </Avatar>
                            <Typography variant="body2" fontWeight="medium">
                              {hostname}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label={metrics.region || 'Unknown'} 
                            sx={{
                              backgroundColor: `${theme.palette.primary.main}1a`,
                              color: theme.palette.primary.main,
                              fontWeight: 'medium',
                              borderRadius: '6px',
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            status={metrics.status || 'inactive'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <ResourceProgress
                              value={metrics.cpu || 0}
                              size="small"
                              showPercentage={true}
                              width={100}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <ResourceProgress
                              value={metrics.memory || 0}
                              size="small"
                              showPercentage={true}
                              width={100}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <ResourceProgress
                              value={metrics.disk || 0}
                              size="small"
                              showPercentage={true}
                              width={100}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Link 
                              to={`/region/${metrics.region}`}
                              style={{ textDecoration: 'none' }}
                            >
                              <Button 
                                variant="contained" 
                                size="small" 
                                sx={{
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#fff',
                                  fontWeight: 'medium',
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                }}
                              >
                                View Metrics
                              </Button>
                            </Link>
                            <IconButton
                              size="small"
                              sx={{
                                backgroundColor: `${theme.palette.background.default}80`,
                                color: theme.palette.text.secondary,
                                borderRadius: '8px',
                                '&:hover': {
                                  backgroundColor: `${theme.palette.primary.main}1a`,
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </LoadingOverlay>
    </Box>
  );
}